import { getToken } from "../authorization/auth";
import { API_ENDPOINT, CN_METHOD, CN_TYPES } from "../authorization/const";

const sum = (a, b) => a + b;

const backgroundColor = [
    'rgba(153, 102, 255, 0.2)',  // violet
    'rgba(75, 192, 192, 0.2)',   // teal
    'rgba(54, 162, 235, 0.2)',   // blue
    'rgba(255, 99, 132, 0.2)',   // pink/red
    'rgba(255, 159, 64, 0.2)',   // orange
    'rgba(255, 205, 86, 0.2)',   // yellow
    'rgba(201, 203, 207, 0.2)',  // gray
    'rgba(100, 181, 246, 0.2)',  // sky blue
    'rgba(174, 213, 129, 0.2)',  // light green
    'rgba(240, 98, 146, 0.2)'    // rose pink
];

const borderColor = [
    'rgb(153, 102, 255)',
    'rgb(75, 192, 192)',
    'rgb(54, 162, 235)',
    'rgb(255, 99, 132)',
    'rgb(255, 159, 64)',
    'rgb(255, 205, 86)',
    'rgb(201, 203, 207)',
    'rgb(100, 181, 246)',
    'rgb(174, 213, 129)',
    'rgb(240, 98, 146)'
];
/**
 * 
 * @param {string} text Title of the graph
 * @param {boolean} vertical X-axis legends will be vertical
 * @param {boolean} stack Stack the multiple bars
 * @returns 
 */
export function getOptions(text, vertical = false, stack = true) {
    return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
        },
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: text,
            },
        },
        scales: {
            x: {
                ticks: (vertical ? { maxRotation: 90, minRotation: 90 } : { maxRotation: 90, minRotation: 0 }),
                stacked: stack,
            },
            y: {
                // ticks: { stepSize: 1 },
                min: 0,
            }
        }
    };
}

export function makeGraph(label, datalist, labels) {
    const data = [];
    let color = 0;
    datalist.forEach((itm, idx) => {
        if (itm.reduce(sum, 0)) {
            data.push({
                label: labels[idx],
                data: itm,
                backgroundColor: backgroundColor[color],
                borderColor: borderColor[color],
                borderWidth: 1,
                cubicInterpolationMode: 'monotone',
                tension: 0.4
            });
            color++;
        }
    });
    return {
        labels: label,
        datasets: data
    };
}

export function methodTransform(data) {
    const methods = CN_METHOD;
    const result = { span: [] };

    // Collect unique month_year values in order
    const monthSet = new Set();
    data.forEach(item => monthSet.add(item.month_year));
    result.span = Array.from(monthSet);

    // Initialize method and count_method arrays
    for (const method of methods) {
        result[method.toLowerCase()] = Array(result.span.length).fill(0);
        result[`count_${method.toLowerCase()}`] = Array(result.span.length).fill(0);
    }

    // Fill in values
    data.forEach(item => {
        const monthIndex = result.span.indexOf(item.month_year);
        const methodKey = item.method.toLowerCase();

        if (methods.includes(item.method)) {
            result[methodKey][monthIndex] += item.total_amount;
            result[`count_${methodKey}`][monthIndex] += item.total_bookings;
        } else {
            result["other"][monthIndex] += item.total_amount;
            result["count_other"][monthIndex] += item.total_bookings;
        }
    });

    return result;
}

export function groupCashNoteData(data, groups) {
    const methods = groups;
    const result = { span: [] };

    // Collect unique month_year values in order
    const monthSet = new Set();
    data.forEach(item => monthSet.add(item.month_year));
    result.span = Array.from(monthSet);

    // Initialize method and count_method arrays
    for (const method of methods) {
        result[method.toLowerCase()] = Array(result.span.length).fill(0);
        result[`count_${method.toLowerCase()}`] = Array(result.span.length).fill(0);
    }

    // Fill in values
    data.forEach(item => {
        const monthIndex = result.span.indexOf(item.month_year);
        const methodKey = item.expense.toLowerCase();

        if (methods.includes(item.expense)) {
            result[methodKey][monthIndex] += item.amount;
            result[`count_${methodKey}`][monthIndex] += item.amount;
        } else {
            result["other"][monthIndex] += item.amount;
            result["count_other"][monthIndex] += item.amount;
        }
    });

    return result;
}

export function docTypeTransform(data) {
    const doc_types = CN_TYPES;
    const result = { span: [] };

    // Collect unique month_year values in order
    const monthSet = new Set();
    data.forEach(item => monthSet.add(item.month_year));
    result.span = Array.from(monthSet);

    // Initialize doc_type and count_doc_type arrays
    for (const doc_type of doc_types) {
        result[doc_type.toLowerCase()] = Array(result.span.length).fill(0);
        result[`count_${doc_type.toLowerCase()}`] = Array(result.span.length).fill(0);
    }

    // Fill in values
    data.forEach(item => {
        const monthIndex = result.span.indexOf(item.month_year);
        const doc_typeKey = item.doc_type.toLowerCase();

        if (doc_types.includes(item.doc_type)) {
            result[doc_typeKey][monthIndex] += item.total_amount;
            result[`count_${doc_typeKey}`][monthIndex] += item.total_bookings;
        } else {
            result["other"][monthIndex] += item.total_amount;
            result["count_other"][monthIndex] += item.total_bookings;
        }
    });

    return result;
}

export async function getDeskData(json) {
    const res = await fetch(API_ENDPOINT + "analysis/day-cn/" + getToken());
    if (res.status === 200)
        return await res.json();
    throw await res.json();
}

export async function getOrgData(json) {
    const res = await fetch(API_ENDPOINT + "analysis/org-cn/" + getToken());
    if (res.status === 200)
        return await res.json();
    throw await res.json();
}

export async function getFinData(json) {
    const res = await fetch(API_ENDPOINT + "analysis/finance/" + getToken());
    if (res.status === 200)
        return await res.json();
    throw await res.json();
}

export async function getTransData(json) {
    const res = await fetch(API_ENDPOINT + "analysis/transaction/" + getToken());
    if (res.status === 200)
        return await res.json();
    throw await res.json();
}
